import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api, type AuthCheckResponse } from '../api/client.ts'

export type UserRole = 'admin' | 'moderator' | 'partner' | 'client'

export interface User {
  username: string
  role: UserRole
  partnerTag: string
	impersonating: boolean
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; partner_tag?: string }>
  register: (username: string, password: string) => Promise<{ success: boolean; message: string }>
  logout: () => Promise<void>
  isAdmin: boolean
  isModerator: boolean
  isPartner: boolean
  isClient: boolean
  canManageUsers: boolean
  canManageCars: boolean
  canManageNews: boolean
  canViewLogs: boolean
  canManageActivations: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchMe = useCallback(async () => {
    try {
      const res = (await api.checkAuth()) as AuthCheckResponse
      if (res.authenticated && res.username && res.role) {
		setUser({ username: res.username, role: res.role as UserRole, partnerTag: res.partner_tag || '', impersonating: !!res.impersonating })
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMe()
  }, [fetchMe])

  const login = async (username: string, password: string) => {
    const res = await api.login(username, password)
    if (res.success && res.username && res.role) {
		setUser({ username: res.username, role: res.role as UserRole, partnerTag: res.partner_tag || '', impersonating: false })
    }
    return res
  }

  const register = async (username: string, password: string) => {
    const res = await api.register(username, password)
    if (res.success && res.username) {
		setUser({ username: res.username, role: 'client', partnerTag: '', impersonating: false })
    }
    return res
  }

  const logout = async () => {
    await api.logout()
    setUser(null)
  }

  // Role checks
  const isAdmin = user?.role === 'admin'
  const isModerator = user?.role === 'moderator'
  const isPartner = user?.role === 'partner'
  const isClient = user?.role === 'client'
  const canManageUsers = isAdmin
  const canManageCars = isAdmin || isModerator || isPartner
  const canManageNews = isAdmin || isModerator
  const canViewLogs = isAdmin || isModerator
  const canManageActivations = isAdmin || isModerator || isPartner

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      isAdmin, isModerator, isPartner, isClient,
      canManageUsers, canManageCars, canManageNews, canViewLogs, canManageActivations,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}
